import { useMemo } from 'react'
import * as THREE from 'three'
import type { WeaponClass } from './weaponTypes'

// ── Helpers ──────────────────────────────────────────────────────────

/** Push a box (8 verts, 12 tris) into the arrays. Returns base index. */
function pushBox(
  positions: number[], cols: number[], indices: number[],
  cx: number, cy: number, cz: number,
  sx: number, sy: number, sz: number,
  r: number, g: number, b: number,
): number {
  const i = positions.length / 3
  const hx = sx / 2, hy = sy / 2, hz = sz / 2
  // 8 corners
  positions.push(
    cx - hx, cy - hy, cz - hz, cx + hx, cy - hy, cz - hz,
    cx + hx, cy + hy, cz - hz, cx - hx, cy + hy, cz - hz,
    cx - hx, cy - hy, cz + hz, cx + hx, cy - hy, cz + hz,
    cx + hx, cy + hy, cz + hz, cx - hx, cy + hy, cz + hz,
  )
  for (let j = 0; j < 8; j++) cols.push(r, g, b)
  // 6 faces (2 tris each)
  indices.push(
    i, i+1, i+5, i, i+5, i+4,    // bottom
    i+2, i+3, i+7, i+2, i+7, i+6, // top
    i, i+3, i+2, i, i+2, i+1,    // front (-z)
    i+4, i+5, i+6, i+4, i+6, i+7, // back (+z)
    i, i+4, i+7, i, i+7, i+3,    // left (-x)
    i+1, i+2, i+6, i+1, i+6, i+5, // right (+x)
  )
  return i
}

// ── Cutlass ──────────────────────────────────────────────────────────

function buildCutlass(): THREE.BufferGeometry {
  const geo = new THREE.BufferGeometry()
  const positions: number[] = []
  const cols: number[] = []
  const indices: number[] = []

  // Blade: 3D curved strip with thickness (top face, bottom face, edge)
  const segments = 12
  const bladeLen = 1.3
  const bladeThick = 0.025     // z-axis thickness
  const bladeHeight = 0.16     // y-axis width of blade

  // Build blade cross-section at each segment — two rows of verts (top edge, bottom edge)
  // for front face (+z) and back face (-z), plus the sharp edge
  // Total 4 verts per cross-section: top-front, bottom-front, top-back, bottom-back
  for (let i = 0; i <= segments; i++) {
    const t = i / segments
    const x = t * bladeLen
    // Curved profile: gentle S-curve typical of cutlass
    const curve = Math.sin(t * Math.PI * 0.45) * 0.10 + t * 0.02
    const taper = 1 - t * 0.35  // blade narrows toward tip
    const edgeTaper = 1 - t * 0.6 // edge gets thinner toward tip
    const h = bladeHeight * taper * 0.5
    const thick = bladeThick * edgeTaper * 0.5

    // Spine (top edge - thicker)
    positions.push(x, curve + h, -thick * 1.2)  // top-back
    positions.push(x, curve + h, thick * 1.2)   // top-front
    // Edge (bottom - knife edge, nearly zero thickness)
    positions.push(x, curve - h, -thick * 0.1)  // bottom-back
    positions.push(x, curve - h, thick * 0.1)   // bottom-front

    // Colors: spine is darker steel, edge is brighter (sharpened)
    const spineR = 0.52 + t * 0.06, spineG = 0.54 + t * 0.06, spineB = 0.58 + t * 0.06
    const edgeR = 0.82 + t * 0.08, edgeG = 0.84 + t * 0.08, edgeB = 0.88 + t * 0.06
    cols.push(spineR, spineG, spineB) // top-back
    cols.push(spineR, spineG, spineB) // top-front
    cols.push(edgeR, edgeG, edgeB)    // bottom-back
    cols.push(edgeR, edgeG, edgeB)    // bottom-front
  }

  // Connect blade segments with faces
  for (let i = 0; i < segments; i++) {
    const a = i * 4
    const b = (i + 1) * 4

    // Front face (+z side): top-front → bottom-front
    indices.push(a+1, b+1, b+3, a+1, b+3, a+3)
    // Back face (-z side): bottom-back → top-back
    indices.push(a+2, b+2, b+0, a+2, b+0, a+0)
    // Spine (top): top-back → top-front
    indices.push(a+0, b+0, b+1, a+0, b+1, a+1)
    // Edge (bottom): bottom-front → bottom-back
    indices.push(a+3, b+3, b+2, a+3, b+2, a+2)
  }

  // Tip cap
  const tipBase = segments * 4
  const tipIdx = positions.length / 3
  const lastT = 1.0
  const tipX = bladeLen + 0.06
  const tipCurve = Math.sin(lastT * Math.PI * 0.45) * 0.10 + lastT * 0.02
  positions.push(tipX, tipCurve, 0) // single tip point
  cols.push(0.92, 0.94, 0.96)
  indices.push(tipBase + 0, tipBase + 1, tipIdx)
  indices.push(tipBase + 1, tipBase + 3, tipIdx)
  indices.push(tipBase + 3, tipBase + 2, tipIdx)
  indices.push(tipBase + 2, tipBase + 0, tipIdx)

  // Guard (brass cross piece)
  pushBox(positions, cols, indices,
    -0.01, 0, 0,    // center
    0.04, 0.28, 0.10, // size: thin X, tall Y, some Z depth
    0.72, 0.58, 0.22)

  // Handle (leather-wrapped grip)
  pushBox(positions, cols, indices,
    -0.20, 0, 0,
    0.30, 0.06, 0.055,
    0.32, 0.20, 0.10)

  // Pommel (brass ball-ish end)
  pushBox(positions, cols, indices,
    -0.37, 0, 0,
    0.05, 0.08, 0.07,
    0.68, 0.55, 0.20)

  geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3))
  geo.setAttribute('color', new THREE.Float32BufferAttribute(cols, 3))
  geo.setIndex(indices)
  geo.computeVertexNormals()
  return geo
}

// ── Boarding Axe ────────────────────────────────────────────────────

function buildBoardingAxe(): THREE.BufferGeometry {
  const geo = new THREE.BufferGeometry()
  const positions: number[] = []
  const cols: number[] = []
  const indices: number[] = []

  // Shaft
  pushBox(positions, cols, indices,
    0.75, 0, 0, 1.5, 0.06, 0.06,
    0.45, 0.30, 0.15)

  // Axe head — wedge
  const ai = positions.length / 3
  const base = 1.35
  // Back face (thick, attached to shaft)
  positions.push(base, -0.03, -0.025, base, -0.03, 0.025)
  positions.push(base, 0.03, -0.025, base, 0.03, 0.025)
  // Top cutting edge
  positions.push(base + 0.28, 0.22, -0.005, base + 0.28, 0.22, 0.005)
  // Bottom spike
  positions.push(base + 0.20, -0.16, -0.005, base + 0.20, -0.16, 0.005)
  for (let i = 0; i < 8; i++) cols.push(0.50, 0.50, 0.55)
  // Front face
  indices.push(ai+1, ai+3, ai+5, ai+1, ai+5, ai+7)
  // Back face
  indices.push(ai+0, ai+4, ai+2, ai+0, ai+6, ai+4)
  // Top face
  indices.push(ai+2, ai+4, ai+5, ai+2, ai+5, ai+3)
  // Bottom face
  indices.push(ai+0, ai+1, ai+7, ai+0, ai+7, ai+6)
  // Outer edge
  indices.push(ai+4, ai+6, ai+7, ai+4, ai+7, ai+5)

  // Spike on back of head
  pushBox(positions, cols, indices,
    base - 0.08, 0, 0, 0.12, 0.04, 0.03,
    0.50, 0.50, 0.53)

  // Leather grip wrap
  pushBox(positions, cols, indices,
    0.12, 0, 0, 0.22, 0.07, 0.07,
    0.35, 0.22, 0.12)

  geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3))
  geo.setAttribute('color', new THREE.Float32BufferAttribute(cols, 3))
  geo.setIndex(indices)
  geo.computeVertexNormals()
  return geo
}

// ── Flintlock Pistol ────────────────────────────────────────────────

function buildFlintlock(): THREE.BufferGeometry {
  const geo = new THREE.BufferGeometry()
  const positions: number[] = []
  const cols: number[] = []
  const indices: number[] = []

  // Barrel
  pushBox(positions, cols, indices,
    0.30, 0, 0, 0.60, 0.045, 0.045,
    0.30, 0.30, 0.33)

  // Muzzle flare
  pushBox(positions, cols, indices,
    0.62, 0, 0, 0.03, 0.06, 0.06,
    0.35, 0.35, 0.38)

  // Grip (angled down)
  const gi = positions.length / 3
  const angle = -0.65
  const cosA = Math.cos(angle), sinA = Math.sin(angle)
  const gLen = 0.25, gw = 0.04, gd = 0.05
  const dx = -gLen * cosA, dy = -gLen * sinA

  positions.push(
    0.02, -gw, -gd,   dx, -gw + dy, -gd,
    dx, gw + dy, -gd,  0.02, gw, -gd,
    0.02, -gw, gd,    dx, -gw + dy, gd,
    dx, gw + dy, gd,   0.02, gw, gd,
  )
  for (let i = 0; i < 8; i++) cols.push(0.42, 0.28, 0.14)
  indices.push(gi, gi+1, gi+5, gi, gi+5, gi+4)
  indices.push(gi+2, gi+3, gi+7, gi+2, gi+7, gi+6)
  indices.push(gi+1, gi+2, gi+6, gi+1, gi+6, gi+5)
  indices.push(gi, gi+4, gi+7, gi, gi+7, gi+3)
  indices.push(gi+4, gi+5, gi+6, gi+4, gi+6, gi+7)

  // Lock mechanism
  pushBox(positions, cols, indices,
    0.12, 0.035, 0, 0.12, 0.05, 0.025,
    0.50, 0.50, 0.53)

  // Trigger
  pushBox(positions, cols, indices,
    0.10, -0.04, 0, 0.02, 0.04, 0.015,
    0.45, 0.45, 0.48)

  // Trigger guard
  pushBox(positions, cols, indices,
    0.10, -0.06, 0, 0.14, 0.015, 0.02,
    0.48, 0.48, 0.50)

  geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3))
  geo.setAttribute('color', new THREE.Float32BufferAttribute(cols, 3))
  geo.setIndex(indices)
  geo.computeVertexNormals()
  return geo
}

// ── Musket ──────────────────────────────────────────────────────────

function buildMusket(): THREE.BufferGeometry {
  const geo = new THREE.BufferGeometry()
  const positions: number[] = []
  const cols: number[] = []
  const indices: number[] = []

  // Long barrel
  pushBox(positions, cols, indices,
    0.80, 0.015, 0, 1.60, 0.040, 0.040,
    0.28, 0.28, 0.32)

  // Muzzle
  pushBox(positions, cols, indices,
    1.62, 0.015, 0, 0.03, 0.055, 0.055,
    0.32, 0.32, 0.36)

  // Stock (wood, tapers)
  const si = positions.length / 3
  const sLen = 0.70, sw = 0.045, sh = 0.065
  positions.push(
    -sLen, -sh, -sw,   0.02, -0.02, -sw,
    0.02, 0.02, -sw,   -sLen, sh * 0.55, -sw,
    -sLen, -sh, sw,    0.02, -0.02, sw,
    0.02, 0.02, sw,    -sLen, sh * 0.55, sw,
  )
  for (let i = 0; i < 8; i++) cols.push(0.42, 0.30, 0.16)
  indices.push(si, si+1, si+5, si, si+5, si+4)
  indices.push(si+2, si+3, si+7, si+2, si+7, si+6)
  indices.push(si+1, si+2, si+6, si+1, si+6, si+5)
  indices.push(si, si+4, si+7, si, si+7, si+3)
  indices.push(si+4, si+5, si+6, si+4, si+6, si+7)
  indices.push(si, si+1, si+2, si, si+2, si+3) // cap

  // Trigger guard
  pushBox(positions, cols, indices,
    0.16, -0.045, 0, 0.12, 0.015, 0.02,
    0.50, 0.50, 0.53)

  // Trigger
  pushBox(positions, cols, indices,
    0.16, -0.03, 0, 0.015, 0.03, 0.012,
    0.48, 0.48, 0.50)

  // Bayonet lug
  pushBox(positions, cols, indices,
    1.50, 0.04, 0, 0.06, 0.02, 0.015,
    0.35, 0.35, 0.38)

  geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3))
  geo.setAttribute('color', new THREE.Float32BufferAttribute(cols, 3))
  geo.setIndex(indices)
  geo.computeVertexNormals()
  return geo
}

// ── Harpoon ─────────────────────────────────────────────────────────

function buildHarpoon(): THREE.BufferGeometry {
  const geo = new THREE.BufferGeometry()
  const positions: number[] = []
  const cols: number[] = []
  const indices: number[] = []

  // Shaft
  pushBox(positions, cols, indices,
    0.90, 0, 0, 1.80, 0.04, 0.04,
    0.50, 0.38, 0.22)

  // Grip wrapping (darker section)
  pushBox(positions, cols, indices,
    0.12, 0, 0, 0.25, 0.05, 0.05,
    0.35, 0.25, 0.14)

  // Spearhead (diamond)
  const hi = positions.length / 3
  const tipBase = 1.82
  const tipEnd = tipBase + 0.22
  // 6 vertices: 4 cross-section + tip + base center
  positions.push(
    tipBase, 0.07, 0,    // top
    tipBase, -0.05, 0,   // bottom
    tipBase, 0, 0.04,    // front
    tipBase, 0, -0.04,   // back
    tipEnd, 0.01, 0,     // tip
  )
  for (let i = 0; i < 5; i++) cols.push(0.60, 0.60, 0.65)
  indices.push(hi+0, hi+2, hi+4)
  indices.push(hi+2, hi+1, hi+4)
  indices.push(hi+1, hi+3, hi+4)
  indices.push(hi+3, hi+0, hi+4)

  // Barbs (two small triangular fins)
  const b1 = positions.length / 3
  positions.push(
    tipBase + 0.06, 0.07, 0,
    tipBase + 0.15, 0.13, 0.005,
    tipBase + 0.12, 0.07, 0,
  )
  for (let i = 0; i < 3; i++) cols.push(0.55, 0.55, 0.60)
  indices.push(b1, b1+1, b1+2)

  const b2 = positions.length / 3
  positions.push(
    tipBase + 0.06, -0.05, 0,
    tipBase + 0.15, -0.11, 0.005,
    tipBase + 0.12, -0.05, 0,
  )
  for (let i = 0; i < 3; i++) cols.push(0.55, 0.55, 0.60)
  indices.push(b2, b2+1, b2+2)

  // Rope coil at base (decorative rings)
  pushBox(positions, cols, indices,
    0.30, 0, 0, 0.04, 0.06, 0.06,
    0.60, 0.55, 0.40)
  pushBox(positions, cols, indices,
    0.36, 0, 0, 0.04, 0.06, 0.06,
    0.58, 0.53, 0.38)

  geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3))
  geo.setAttribute('color', new THREE.Float32BufferAttribute(cols, 3))
  geo.setIndex(indices)
  geo.computeVertexNormals()
  return geo
}

// ── Material + factory ──────────────────────────────────────────────

const WEAPON_MATERIAL = new THREE.MeshStandardMaterial({
  vertexColors: true,
  metalness: 0.5,
  roughness: 0.45,
})

const builders: Record<WeaponClass, () => THREE.BufferGeometry> = {
  cutlass: buildCutlass,
  boarding_axe: buildBoardingAxe,
  flintlock: buildFlintlock,
  musket: buildMusket,
  harpoon: buildHarpoon,
}

interface WeaponMeshProps {
  weapon: WeaponClass
  visible?: boolean
}

export function WeaponMesh({ weapon, visible = true }: WeaponMeshProps) {
  const geometry = useMemo(() => builders[weapon](), [weapon])

  if (!visible) return null

  return (
    <mesh geometry={geometry} material={WEAPON_MATERIAL} />
  )
}
